import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useExtensionStore, getBridge } from '../store';
import ExtensionTreeNode from './ExtensionTreeNode';
import { resolveActionIcon, resolveIcon } from './icons';

// Recursively keeps only nodes whose label matches `query`, or that have a
// matching descendant among already-loaded children — mirrors Collections'
// filterCollectionForSearch, but generic over the TreeNode contract. Lazy
// branches not yet expanded can't be searched into (nothing to look at
// client-side yet), matching how a file tree only searches what's loaded.
function filterTree(nodes, query) {
  const result = [];
  for (const node of nodes) {
    const isBranch = node.kind === 'branch';
    const rawChildren = isBranch ? (node.children === 'lazy' ? node.resolvedChildren || [] : node.children || []) : [];
    const filteredChildren = rawChildren.length ? filterTree(rawChildren, query) : [];
    const selfMatches = node.label.toLowerCase().includes(query);
    if (selfMatches || filteredChildren.length > 0) {
      result.push(isBranch ? { ...node, children: filteredChildren } : node);
    }
  }
  return result;
}

/**
 * Host-owned renderer for an extension's `contributes.sidebarSections` entry.
 * Mirrors SchemaSidebar's embedded collapsible-section pattern exactly (same
 * classnames, same collapse/expand-state persistence convention) so an
 * extension's tree is visually indistinguishable from Collections/Schemas —
 * extensions send data via `sdk.tree.publish`, they never render their own
 * DOM here.
 *
 * The section title + header actions (refresh / new connection) no longer
 * render here — they live in the app titlebar (see ExtensionTitleBarActions),
 * so this component renders only the node tree beneath the search row.
 */
const EMPTY_NODES = [];

export default function ExtensionTreeSection({
  extensionId,
  sectionId,
  workspaceId,
  searchTerm = '',
  emptyIcon,
  emptyTitle,
  emptyHint,
}) {
  const nodes = useExtensionStore((s) => s.treeData[sectionId]) || EMPTY_NODES;

  const expandKey = `ext-sidebar-expanded:${extensionId}:${sectionId}:${workspaceId}`;

  // The search input lives in ExtensionSidebarSections' fixed header (mirrors
  // the Collections sidebar layout); this section only consumes the term.
  const query = searchTerm.trim().toLowerCase();
  const searching = query.length > 0;
  const filteredNodes = useMemo(() => (searching ? filterTree(nodes, query) : nodes), [nodes, query, searching]);

  const [expandedIds, setExpandedIds] = useState(() => new Set());
  /**
   * Node ids the user expanded BY HAND this session.
   *
   * `expandedIds` is restored from localStorage, so after any republish the
   * effect below re-requests children for everything that was open when the
   * app last ran. The guest cannot tell that apart from a chevron click, and
   * the DB Manager reasonably treats an expand of a connection root as "dial
   * this connection" — so unlocking the vault silently reconnected every
   * database the user had ever left expanded.
   *
   * A ref, not state: it only ever qualifies a message that some other change
   * already triggers, and re-rendering when it changes would be noise.
   */
  const userExpandedRef = useRef(new Set());

  // `__tree_expand__` used to fire only from a chevron click, so a lazy node
  // that was *already* expanded (persisted expandedIds from a previous
  // session, or a fresh publish after reconnect/refresh resetting `loaded`)
  // rendered open with no children until the user collapsed and re-expanded
  // it. Whenever tree data or expansion changes, ask the guest to load every
  // visible expanded lazy branch that isn't loaded yet. `requestedRef`
  // de-dupes while a load is in flight; the id is released once the node
  // arrives loaded so a later refresh (loaded reset to false) re-requests.
  const requestedRef = useRef(new Set());
  useEffect(() => {
    const bridge = getBridge(extensionId);
    if (!bridge) return;
    const walk = (list) => {
      for (const node of list) {
        if (node.kind !== 'branch') continue;
        const isOpen = expandedIds.has(node.id);
        if (node.children === 'lazy') {
          if (node.loaded) {
            requestedRef.current.delete(node.id);
          } else if (isOpen && !requestedRef.current.has(node.id)) {
            requestedRef.current.add(node.id);
            bridge.postToGuest('__tree_expand__', {
              sectionId,
              nodeId: node.id,
              // Additive and ignorable: an extension that does not care keeps
              // loading children either way. It matters only where expanding
              // has a SIDE EFFECT beyond reading — connecting to a database
              // being the case in hand.
              user: userExpandedRef.current.has(node.id),
            });
          }
          if (isOpen) walk(node.resolvedChildren || []);
        } else if (isOpen) {
          walk(node.children || []);
        }
      }
    };
    walk(nodes);
  }, [nodes, expandedIds, extensionId, sectionId]);

  // sdk.tree.expand: open what the guest asked for (the DB Manager after a
  // user connect: connection -> Tables). Lazy levels load through the effect
  // above as each one appears.
  const expandRequest = useExtensionStore((s) => s.treeExpandRequests?.[sectionId]);
  useEffect(() => {
    if (!expandRequest) return;
    useExtensionStore.getState().clearTreeExpand(sectionId);
    setExpandedIds((prev) => {
      if (expandRequest.every((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      expandRequest.forEach((id) => next.add(id));
      try {
        localStorage.setItem(expandKey, JSON.stringify([...next]));
      } catch {
        /* ignore quota errors */
      }
      return next;
    });
  }, [expandRequest, expandKey, sectionId]);

  useEffect(() => {
    if (!workspaceId) return;
    try {
      const raw = localStorage.getItem(expandKey);
      const ids = raw ? JSON.parse(raw) : [];
      if (Array.isArray(ids)) setExpandedIds(new Set(ids.map(String)));
    } catch {
      /* ignore corrupt state */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandKey, workspaceId]);

  const handleToggleNode = useCallback(
    (node) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(node.id)) {
          next.delete(node.id);
          // Collapsing releases the in-flight guard so collapse + re-expand
          // acts as a manual retry after a failed lazy load.
          requestedRef.current.delete(node.id);
          // And forgets the intent, so a later republish does not treat the
          // node as user-expanded on the strength of a click long undone.
          userExpandedRef.current.delete(node.id);
        } else {
          next.add(node.id);
          // This one IS the user asking.
          userExpandedRef.current.add(node.id);
        }
        try {
          localStorage.setItem(expandKey, JSON.stringify([...next]));
        } catch {
          /* ignore quota errors */
        }
        return next;
      });
      // Lazy-load requests are posted by the expandedIds/nodes effect above —
      // the single request path (posting here too would double-fire queries).
    },
    [expandKey]
  );

  const handleNodeClick = useCallback(
    (node) => {
      getBridge(extensionId)?.postToGuest('__tree_node_click__', { sectionId, nodeId: node.id });
    },
    [extensionId, sectionId]
  );

  const handleAction = useCallback(
    (node, actionId) => {
      getBridge(extensionId)?.postToGuest('__tree_action__', { sectionId, nodeId: node.id, actionId });
    },
    [extensionId, sectionId]
  );

  // Node actions render behind a Collections-style "…" dropdown (same trigger,
  // same menu classnames) instead of inline hover icons — opened from the row's
  // "…" button or a right-click. `tooltip` doubles as the item label.
  const [nodeMenu, setNodeMenu] = useState(null); // { x, y, node } | null
  const nodeMenuRef = useRef(null);

  const openNodeMenu = useCallback((e, node) => {
    setNodeMenu({ x: e.clientX, y: e.clientY, node });
  }, []);

  // Capitalized local so the empty state can use JSX like every other icon
  // call site here (cf. ActionIcon below, and ExtensionTreeNode).
  const EmptyIcon = resolveIcon(emptyIcon);

  // Same click-away convention as Sidebar's collections context menu.
  useEffect(() => {
    if (!nodeMenu) return undefined;
    const handleClickOutside = (e) => {
      if (nodeMenuRef.current && nodeMenuRef.current.contains(e.target)) return;
      setNodeMenu(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setNodeMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [nodeMenu]);

  return (
    // preventDefault too: stopPropagation alone kept the event from reaching
    // the app's document-level handler (TextContextMenu), which is what
    // suppresses the browser's own menu — so right-clicking any row without
    // actions (Tables, Views, a column…) or the empty area showed Back/Reload/Inspect.
    <div className="pt-1" onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      {nodes.length === 0 ? (
        // Same markup/classes as the Request History empty state (History.js)
        // so an extension's empty sidebar reads as part of the app rather than
        // as a stray line of grey text. The icon comes from the section's own
        // `contributes.sidebarSections[].icon`; resolveIcon falls back to
        // Circle, so a section that declares none still renders correctly.
        <div className="py-8 text-center text-gray-500 dark:text-zinc-400">
          <div className="sidebar-empty-icon-wrap">
            <div className="sidebar-empty-icon">
              <EmptyIcon size={16} />
            </div>
          </div>
          <p className="mb-1 text-xs font-medium text-gray-600 dark:text-zinc-200">{emptyTitle}</p>
          <p className="text-xs text-gray-500 dark:text-zinc-400">{emptyHint}</p>
        </div>
      ) : searching && filteredNodes.length === 0 ? (
        <p className="px-2 py-3 text-xs text-gray-400 dark:text-zinc-500">No matches.</p>
      ) : (
        filteredNodes.map((node) => (
          <ExtensionTreeNode
            key={node.id}
            node={node}
            depth={0}
            expandedIds={expandedIds}
            searchActive={searching}
            onToggle={handleToggleNode}
            onNodeClick={handleNodeClick}
            onAction={handleAction}
            onActionsMenu={openNodeMenu}
          />
        ))
      )}
      {nodeMenu &&
        // Portaled to <body>: the sidebar lives inside a split pane whose
        // splitter forms its own stacking context, so a `fixed z-50` menu
        // rendered inline would paint underneath it (same reason the results
        // grid's context menu portals).
        ReactDOM.createPortal(
          <div
            ref={nodeMenuRef}
            className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
            style={{ left: nodeMenu.x, top: nodeMenu.y }}
            onContextMenu={(e) => e.preventDefault()}
          >
            {(nodeMenu.node.actions || []).map((action) => {
              const ActionIcon = resolveActionIcon(action.icon);
              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => {
                    handleAction(nodeMenu.node, action.id);
                    setNodeMenu(null);
                  }}
                  className={`ctx-menu-btn${action.icon === 'trash' || action.icon === 'eraser' ? ' ctx-menu-btn--danger' : ''}`}
                >
                  <ActionIcon size={14} />
                  <span>{action.tooltip}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
